import { useState, createContext, useContext, useCallback, useMemo } from "react";
import { Button, Menu } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PropTypes from "prop-types";

/**
 * DropdownContext - Context for dropdown menu state and controls
 */
const DropdownContext = createContext(null);

/**
 * useDropdown - Custom hook to access dropdown context
 * Provides handleOpen, handleClose, and open state
 *
 * @returns {Object|null} Dropdown context value with { handleOpen, handleClose, open } or null if not in dropdown
 */
export function useDropdown() {
  return useContext(DropdownContext);
}

Dropdown.propTypes = {
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  buttonProps: PropTypes.object,
  menuProps: PropTypes.object,
};
function Dropdown({ children, disabled, buttonProps, menuProps }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const openMenu = Boolean(anchorEl);
  const handleOpenMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      handleOpen: handleOpenMenu,
      handleClose: handleCloseMenu,
      open: openMenu,
    }),
    [handleOpenMenu, handleCloseMenu, openMenu],
  );

  return (
    <DropdownContext.Provider value={contextValue}>
      <Menu
        open={openMenu}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        {...menuProps}>
        {children}
      </Menu>

      <Button disabled={disabled} variant={"outlined"} onClick={handleOpenMenu} {...buttonProps}>
        {buttonProps?.children}
        <MoreVertIcon />
      </Button>
    </DropdownContext.Provider>
  );
}

export default Dropdown;
